import ASTExplorer from "./ast_explorer.js";

// The Suggester class is designed to provide intelligent suggestions based on
// the context within an Abstract Syntax Tree (AST) for a specific language or
// data structure (e.g., BadgerQL). It utilizes various rules defined in
// `ruleFns` to understand the context of the cursor in the AST and suggest
// relevant tokens, functions, parameters, etc.

// `ruleFns` contains functions that define specific rules used in BadgerQL
// param suggestion logic. These rules help in determining the relevance of
// suggestions based on the current position and context within the AST.
const ruleFns = {
  onlyOnce: (param, selection) => {
    const checkToken = param.token;
    let found = false;
    ASTExplorer.walk(selection, "prev", (e) => {
      if (e.token === checkToken) {
        found = true;
        return true;
      }
    });

    return !found;
  },

  positionAfter: (param, selection, tokenIdx) => {
    if (selection.prev?.tokenIdx >= tokenIdx) {
      return true;
    }

    return false;
  },

  position: (param, selection, tokenIdx) => {
    if (selection.prev?.tokenIdx === tokenIdx) {
      return true;
    }

    return false;
  },

  stopAfter: (param, selection, token) => {
    let stop = false;
    ASTExplorer.walk(selection, "prev", (e) => {
      if (e.token === token) {
        stop = true;
        return true;
      }
    });

    return !stop;
  },

  ignoreIfPrev: (param, selection, ...tokens) => {
    if (tokens.some((token) => selection.prev?.token === token)) {
      return false;
    }

    return true;
  },

  afterExprs: (param, selection, resetToken) => {
    const checkToken = param.token;
    // We have a comma right before, so we can't alias
    if (selection.prev?.token === ",") {
      return false;
      // We are at the end of a function
    } else if (
      selection.prev?.type === "exprFn" &&
      selection.prev?.end === selection.pos - 1
    ) {
      return false;
      // This is right after the BadgerQL function
    } else if (selection.prev?.tokenIdx === 0) {
      return false;
    }

    let ret = true;
    ASTExplorer.walk(selection, "prev", (e) => {
      if (e.token === checkToken) {
        ret = false;
        return true;
      } else if (e.token === ",") {
        return true;
      } else if (resetToken && e.token === resetToken) {
        return true;
      }
    });

    return ret;
  },
};

const lookupRule = (rule) => {
  const r = ruleFns[rule];
  if (r === undefined) {
    throw new Error(`Unknown rule ${rule}`);
  }
  return r;
};

export default class Suggester {
  constructor(ast, cursorOffset, meta, fromInput) {
    this.ast = ast;
    this.cursorOffset = cursorOffset;
    this.meta = meta;

    this.fromInput = fromInput;
    this.selection = ast.findIdx(cursorOffset);
    this.metaCache = {};
  }

  metaEntries(key) {
    if (this.metaCache[key] === undefined) {
      this.metaCache[key] = Object.entries(this.meta[key] || {});
    }

    return this.metaCache[key];
  }

  filterMeta(metaKey, kind, token) {
    let coll = [];

    for (let [name, fn] of this.metaEntries(metaKey)) {
      // Empty token means we should return all
      if (token === "" || token === undefined) {
        coll.push({ kind, rest: name, matched: "", ...fn });
      } else if (name.startsWith(token) && name !== token) {
        const matched = name.slice(0, token.length);
        const rest = name.slice(token.length);

        coll.push({ kind, rest, matched, ...fn });
      }
    }

    return coll;
  }

  filterBadgerQLFunctions(token) {
    return this.filterMeta("badgerql_functions", "badgerFn", token);
  }

  firstToken({ cursor, cursorInfo: { column } }) {
    if (
      cursor === undefined ||
      (cursor?.prev === undefined && cursor?.next !== undefined)
    ) {
      return true;
    } else if (column === 1) {
      return true;
    }

    return false;
  }

  afterLineBreak(cursor) {
    if (cursor?.prev?.type === "lineBreak") return true;
    return false;
  }

  allBadgerQLFunctions() {
    return this.filterMeta("badgerql_functions", "badgerFn", "");
  }

  get rootNode() {
    const badgerFnNode = ASTExplorer.rootFnToken(this.selection);
    return (this.meta.badgerql_functions || {})[badgerFnNode?.token];
  }

  filteredParams(params, selection) {
    if (params === undefined) return [];

    const filtered = params.filter((param) => {
      return param?.rules?.every((rule) => {
        if (Array.isArray(rule)) {
          const [name, ...args] = rule;
          return lookupRule(name)(param, selection, ...args);
        } else {
          return lookupRule(rule)(param, selection);
        }
      });
    });

    if (filtered.length === 0) return [];
    if (selection.token === undefined) {
      return filtered.map((param) => ({
        kind: "params",
        rest: param.token,
        matched: "",
        ...param,
      }));
    } else {
      let coll = [];
      const { token } = selection;

      filtered.forEach((filter) => {
        const { token: name } = filter;
        if (name === "" || name === undefined) {
          coll.push({ kind: "params", ...filter });
        } else if (name.startsWith(token) && name !== token) {
          const matched = name.slice(0, token.length);
          const rest = name.slice(token.length);

          coll.push({ kind: "params", rest, matched, ...filter });
        }
      });

      return coll;
    }
  }

  tokenSuggestion() {
    const { type, token } = this.selection;
    const ret = { type, token };

    if (this.selection.token.startsWith("@")) {
      return {
        ...ret,
        results: this.filterMeta(
          "internal_fields",
          "internalField",
          this.selection.token,
        ),
      };
    }

    let suggestions = [];

    if (this.selection.parent.type === "rootFn") {
      if (this.selection?.prev === undefined) {
        return {
          ...ret,
          results: this.filterBadgerQLFunctions(this.selection.token),
        };
      } else if (this.selection?.prev?.token === "as") {
        return { ...ret, results: [] };
      }

      const params = this.rootNode?.params;

      if (params !== undefined) {
        suggestions = this.filteredParams(params, this.selection);
      }
    }

    // This is a bit of a hack, but we need to be able to match 2 word
    // expression functions
    if (this.selection.prev && this.selection.prev.token !== undefined) {
      suggestions.push(
        ...this.filterMeta(
          "expression_functions",
          "exprFn",
          `${this.selection.prev.token} ${this.selection.token}`,
        ),
      );
    }

    if (this.fromInput && this.selection.idxOffset > 0) {
      const { token, idxOffset } = this.selection;
      const pre = token.slice(0, idxOffset);
      suggestions.push(
        ...this.filterMeta("expression_functions", "exprFn", pre),
      );
    } else {
      suggestions.push(
        ...this.filterMeta(
          "expression_functions",
          "exprFn",
          this.selection.token,
        ),
      );
    }

    return { ...ret, lookupFields: token !== ",", results: suggestions };
  }

  rootFnSuggestion() {
    const cursor = this.selection.cursor;
    // There is nothing in the rootFn yet, so we should
    // suggest all functions
    if (cursor === undefined) {
      return this.allBadgerQLFunctions();
    } else {
      // If this is in first selection and there's a next, we're at the beginning
      // of a rootFn, and should not suggest anything
      if (this.firstToken(this.selection)) {
        return [];
      } else if (cursor?.prev?.token === "as") {
        return [];
      } else {
        let suggestions = this.filteredParams(this.rootNode?.params, cursor);

        if (cursor?.prev?.token !== undefined) {
          suggestions.push(
            ...this.filterMeta(
              "expression_functions",
              "exprFn",
              `${cursor.prev.token} `,
            ),
          );
        }

        return suggestions;
      }
    }
  }

  exprFnSuggestions() {
    const { idxOffset } = this.selection;
    if (idxOffset === 0) return [];
    if (this.fromInput) {
      const { token, idxOffset } = this.selection;
      const pre = token.slice(0, idxOffset);
      return this.filterMeta("expression_functions", "exprFn", pre);
    }
    return [];
  }

  regexSuggestions() {
    if (this.selection.outside) return [];
    let suggestions = [];

    if (ASTExplorer.rootFnToken(this.selection).token === "parse") {
      suggestions.push({
        kind: "regex-capture",
        description: "Regex capture",
        rest: "(?<field>)",
      });
    }

    const pushed = new Set();
    const regex = this.metaEntries("regex").sort(([, { kind }]) => kind);
    regex.forEach(([name, { kind, description, example }]) => {
      if (!pushed.has(kind)) {
        suggestions.push({ template: "header", kind: "header", label: kind });
        pushed.add(kind);
      }
      suggestions.push({
        kind: "regex",
        rest: name,
        description,
        example,
      });
    });

    return suggestions;
  }

  stringSuggestions() {
    const { before, open, idxOffset } = this.selection;

    if (idxOffset > 0 || open || before === "") return [];

    return this.filterMeta("expression_functions", "exprFn", before);
  }

  suggestions() {
    const { type, token } = this.selection;
    let base = { offset: this.cursorOffset, type, token };

    switch (this.selection.type) {
      case "exprFn":
        return { ...base, group: "list", results: this.exprFnSuggestions() };
      case "token":
        return {
          ...base,
          group: "list",
          offset: this.selection.start,
          ...this.tokenSuggestion(),
        };
      case "rootFn":
        return { ...base, group: "list", results: this.rootFnSuggestion() };
      case "regex":
        return { ...base, group: "regex", results: this.regexSuggestions() };
      case "string":
        return { ...base, group: "list", results: this.stringSuggestions() };
    }

    return { ...base, group: "list", results: [] };
  }
}
